// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { apiGatewayClient } from './api'

/**
 * Calls the docs-assistant backend route (POST /docs-assistant/ask).
 * sessionId is omitted from the request body on the first turn of a
 * conversation, then threaded through on subsequent turns for context.
 */
export function askDocsAssistant(question, sessionId) {
  const body = sessionId ? { question, sessionId } : { question }

  return apiGatewayClient()
    .then(client => client.post('/docs-assistant/ask', {}, body, {}))
    .then(({ data }) => data)
}

// sessionStorage (not localStorage or component state): survives a page
// change and a refresh, but clears when the tab closes. The transcript is
// stored with the session id so leaving the page does not hide a conversation
// Bedrock still holds. See the "Session state" decision in
// docs/ai-assistant-runbook.md.
// Instruction used: scope-discipline.instructions.md
const SESSION_STORAGE_KEY = 'docsAssistantSessionId'
const TRANSCRIPT_STORAGE_KEY = 'docsAssistantTranscript'

function readStorage(key) {
  try {
    return sessionStorage.getItem(key) || undefined
  } catch (error) {
    return undefined
  }
}

function writeStorage(key, value) {
  try {
    if (value) {
      sessionStorage.setItem(key, value)
    } else {
      sessionStorage.removeItem(key)
    }
  } catch (error) {
    // Private mode and blocked storage must not prevent the assistant from working.
  }
}

export function getStoredSessionId() {
  return readStorage(SESSION_STORAGE_KEY)
}

export function storeSessionId(sessionId) {
  writeStorage(SESSION_STORAGE_KEY, sessionId)
}

export function getStoredTranscript() {
  const raw = readStorage(TRANSCRIPT_STORAGE_KEY)
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

export function storeTranscript(messages) {
  writeStorage(TRANSCRIPT_STORAGE_KEY, messages && messages.length ? JSON.stringify(messages) : undefined)
}

export function clearStoredSessionId() {
  storeSessionId(undefined)
  storeTranscript([])
}
